import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const resolveApiBase = () => {
  const envBase = process.env.REACT_APP_BACKEND_URL || '';
  const host = window.location.hostname;
  if (host === 'oneglobealliance.com' || host === 'www.oneglobealliance.com' || host === 'uat.oneglobealliance.com') {
    return window.location.origin;
  }
  return envBase || 'http://localhost:8001';
};

const API_BASE = resolveApiBase();

// Create axios instance for admin
const adminApi = axios.create({
  baseURL: API_BASE,
});

// Add auth interceptor
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Single-session: clear admin session when logged in elsewhere
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const detail = error.response?.data?.detail || '';
    const isLoggedInElsewhere =
      error.response?.status === 401 &&
      (typeof detail === 'string' && (detail.includes('elsewhere') || detail.includes('no longer valid')));
    if (isLoggedInElsewhere) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      sessionStorage.setItem('admin_logout_reason', 'logged_in_elsewhere');
      window.location.href = '/admin';
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        try {
          const response = await adminApi.get('/api/admin/auth/me');
          if (response.data) {
            setAdmin(response.data);
            setIsAuthenticated(true);
          }
        } catch (error) {
          console.error('Admin session validation failed:', error);
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const response = await adminApi.post('/api/admin/auth/login', { email, password });
      if (response.data.status === 'success') {
        localStorage.setItem('admin_token', response.data.token);
        localStorage.setItem('admin_user', JSON.stringify(response.data.admin));
        setAdmin(response.data.admin);
        setIsAuthenticated(true);
        return { success: true };
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setAdmin(null);
    setIsAuthenticated(false);
  }, []);

  const hasPermission = useCallback((module, action) => {
    // Super admin has all permissions
    if (admin?.role === 'super_admin') return true;
    if (!admin?.permissions) return false;
    const modulePermissions = admin.permissions[module] || [];
    return modulePermissions.includes(action);
  }, [admin]);

  const value = {
    admin,
    loading,
    isAuthenticated,
    login,
    logout,
    hasPermission,
    api: adminApi
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export { adminApi };
export default AdminAuthContext;
